import React, { useLayoutEffect, useState } from "react";
import { RESIZE_HANDLES } from "./useElementResize";
import { elakayAutoText } from "../dataElakay";


// ---------------------------------------------------------------------------
// editorKit/SelectionOverlay — chrome visuel de sélection partagé : contour,
// étiquette largeur×hauteur, puce "type · parent", poignées de
// redimensionnement et poignée de déplacement.
//
// Se positionne en mesurant le vrai rectangle DOM du nœud sélectionné
// (getBoundingClientRect) relatif à un conteneur — fonctionne donc aussi
// bien pour un élément en position libre que pour un élément dans le flux
// normal (flex/grid/bloc), sans que le renderer récursif ait besoin de rien
// savoir de la sélection.
//
// `targetRef`/`containerRef` : deux refs DOM. Si `targetEl`/`containerEl`
// sont fournis directement (éléments DOM, pas des refs), ils sont utilisés
// à la place — pratique quand l'élément est retrouvé via
// querySelector('[data-pb-node-id="…"]') plutôt que via une ref React.
// ---------------------------------------------------------------------------
export default function SelectionOverlay({
  targetEl, containerEl,
  label, parentLabel,
  showHandles = true,
  handles = RESIZE_HANDLES,
  onHandlePointerDown,
  onMovePointerDown,
  dropHighlight = false,
  color = "#6d28d9",
}) {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!targetEl || !containerEl) { setRect(null); return undefined; }
    const measure = () => {
      const t = targetEl.getBoundingClientRect();
      const c = containerEl.getBoundingClientRect();
      setRect({ left: t.left - c.left, top: t.top - c.top, width: t.width, height: t.height });
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(targetEl);
    }
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [targetEl, containerEl]);

  if (!rect) return null;

  return (
    <div
      style={{
        position: "absolute", left: rect.left, top: rect.top, width: rect.width, height: rect.height,
        pointerEvents: "none", zIndex: 30,
      }}
    >
      <div
        style={{
          position: "absolute", inset: 0, borderRadius: "2px",
          outline: `2px solid ${dropHighlight ? "#059669" : color}`, outlineOffset: "2px",
          background: dropHighlight ? "rgba(5,150,105,0.08)" : "transparent",
          transition: "outline-color 0.1s, background 0.1s",
        }}
      />
      <span
        style={{
          position: "absolute", top: "-20px", left: 0, fontSize: "10px", fontWeight: 700,
          color, background: "#fff", padding: "1px 6px", borderRadius: "5px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)", whiteSpace: "nowrap",
        }}
      >
        {Math.round(rect.width)} × {Math.round(rect.height)}
        {label ? ` · ${label}` : ""}
        {parentLabel ? ` · dans ${parentLabel}` : ""}
      </span>
      {onMovePointerDown && (
        <div
          onPointerDown={onMovePointerDown}
          title={elakayAutoText("h_5de39939ae33cb", "Glisser pour déplacer")}
          style={{
            position: "absolute", top: "-20px", right: 0, width: "16px", height: "16px",
            borderRadius: "4px", background: color, cursor: "grab", pointerEvents: "auto",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div style={{ width: "8px", height: "8px", borderRadius: "1px", border: "1px solid #fff" }} />
        </div>
      )}
      {showHandles && onHandlePointerDown && handles.map(({ h, style }) => (
        <div
          key={h}
          onPointerDown={(e) => onHandlePointerDown(e, h, rect)}
          style={{
            position: "absolute", width: "10px", height: "10px", background: "#fff",
            border: `2px solid ${color}`, borderRadius: "3px", zIndex: 31, pointerEvents: "auto",
            ...style,
          }}
        />
      ))}
    </div>
  );
}
